import React from "react";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import styled from "styled-components";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TimeSplitter from "src/utils/time-splitter";
import { Stack } from "@mui/material";
import { ExecutiveValueFormatterComponent } from "src/scenes/overview/overview-components/executive-value-formatter";

const MetricNumber = styled.p`
    font-size: 32px;
    font-weight: 400;
    margin-top: 0;
    margin-bottom: 0;
`;

const MetricAverage = styled.span`
    font-size: 12px;
    font-weight: 300;
    color: ${({ theme, value, baseline }) => {
        if (baseline === 0 || baseline === null) return theme.palette.text.disabled;
        if (value > baseline) return theme.palette.primary.main;
        if (value < baseline) return theme.palette.warning.dark;
        if (value === baseline) return theme.palette.text.disabled;
        return theme.palette.text.disabled;
    }};
`;

const ComparisonPointer = styled(ArrowForwardIcon)`
    transform: ${({ value, baseline }) => (value > baseline ? "rotate(-45deg)" : "rotate(45deg)")};
    display: ${({ value, baseline }) => (value === baseline || !value ? "none" : "inline")};
    position: relative;
    top: 6px;
    color: ${({ theme, value, baseline }) => {
        if (value > baseline) return theme.palette.primary.main;
        if (value < baseline) return theme.palette.warning.dark;
        return null;
    }};
`;

const ComparisonString = "weekly average";

const SingleFundamentalsMetric = ({
    fetchAction,
    value,
    baseline,
    variant,
    ticketsTransitioned,
    ...otherProps
}) => {
    if (!fetchAction) return null;
    if (variant === "standard") {
        const valueIntl = value && value.toLocaleString("en", { useGrouping: true });
        const baselineIntl = baseline && baseline.toLocaleString("en", { useGrouping: true });
        return (
            <>
                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="30%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.statsBaseline}
                >
                    {!baseline || baseline === 0 || baseline === null ? (
                        <MetricAverage value={1} baseline={1}>
                            No weekly average
                        </MetricAverage>
                    ) : (
                        <MetricAverage value={value} baseline={baseline}>
                            {baselineIntl} {ComparisonString}
                        </MetricAverage>
                    )}
                </ShowWithSkeleton>

                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="70%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.stats}
                >
                    <MetricNumber>
                        {valueIntl}
                        <ComparisonPointer value={value} baseline={baseline} />
                    </MetricNumber>
                </ShowWithSkeleton>
            </>
        );
    }
    if (variant === "tickets-transitioned")
        return (
            <>
                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="30%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.statsBaseline}
                >
                    {!baseline || baseline === 0 || baseline === null ? (
                        <MetricAverage value={1} baseline={1}>
                            No weekly average
                        </MetricAverage>
                    ) : (
                        <MetricAverage value={value} baseline={baseline}>
                            {baseline} {ComparisonString}
                        </MetricAverage>
                    )}
                </ShowWithSkeleton>

                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="70%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.stats}
                >
                    <Stack direction="row" alignItems="center" style={{ width: "100%" }}>
                        <MetricNumber>
                            {value}
                            <ComparisonPointer value={value} baseline={baseline} />
                        </MetricNumber>
                        {ticketsTransitioned && (
                            <span style={{ fontSize: "11px" }}>
                                ({ticketsTransitioned.uniqueIssues} unique issues)
                            </span>
                        )}
                    </Stack>
                </ShowWithSkeleton>
            </>
        );
    if (variant === "percentage")
        return (
            <>
                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="30%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.statsBaseline}
                >
                    {!baseline || baseline === 0 || baseline === null ? (
                        <MetricAverage value={1} baseline={1}>
                            No weekly average
                        </MetricAverage>
                    ) : (
                        <MetricAverage value={value} baseline={baseline}>
                            {(baseline * 100).toFixed(1)}% {ComparisonString}
                        </MetricAverage>
                    )}
                </ShowWithSkeleton>
                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="100%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.stats}
                >
                    <MetricNumber>
                        {(value * 100).toFixed(1)}%
                        <ComparisonPointer value={value} baseline={baseline} />
                    </MetricNumber>
                </ShowWithSkeleton>
            </>
        );
    if (variant === "formatted")
        return (
            <>
                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="30%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.statsBaseline}
                >
                    {!baseline || baseline === 0 || baseline === null ? (
                        <MetricAverage value={1} baseline={1}>
                            No weekly average
                        </MetricAverage>
                    ) : (
                        <MetricAverage value={value} baseline={baseline}>
                            {TimeSplitter(baseline, "stage-duration")} {ComparisonString}
                        </MetricAverage>
                    )}
                </ShowWithSkeleton>
                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="100%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.stats}
                >
                    <MetricNumber>
                        {TimeSplitter(value, "stage-duration")}
                        <ComparisonPointer value={value} baseline={baseline} />
                    </MetricNumber>
                </ShowWithSkeleton>
            </>
        );
    if (variant === "na")
        return (
            <>
                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="30%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.statsBaseline}
                >
                    <MetricAverage value={1} baseline={1}>
                        Non Applicable
                    </MetricAverage>
                </ShowWithSkeleton>

                <ShowWithSkeleton
                    fetchAction={fetchAction}
                    height="70%"
                    width="100%"
                    streamingProperty={fetchAction.data && fetchAction.data.stats}
                >
                    <MetricNumber>{value}</MetricNumber>
                </ShowWithSkeleton>
            </>
        );
    if (variant === "executive") {
        const slopeValue = otherProps.slope;

        const itemType = otherProps.type;

        const itemCustomText = otherProps.customText;

        return (
            <ShowWithSkeleton
                fetchAction={fetchAction}
                variant={undefined}
                height={50}
                width="100%"
                /* eslint-disable-next-line no-restricted-globals */
                streamingProperty={fetchAction && fetchAction.data && fetchAction.data[name]}
            >
                <ExecutiveValueFormatterComponent
                    value={value}
                    type={itemType}
                    isBreakdown={false}
                    customText={itemCustomText}
                    slope={slopeValue}
                />
            </ShowWithSkeleton>
        );
    }
    return null;
};

export default SingleFundamentalsMetric;
